import React, { useEffect, useState } from 'react'
import { isCasinoFavorite, toggleCasinoFavorite } from '../../utils/casinoFavoritesStore'
import {
  resolveMac88LocalImageUrl,
  resolveMac88RemoteImageUrl,
} from '../../utils/mac88LocalImage'
import '../../apollo-icons.css'
import './LotusCasinoGameCard.css'

const API_BASE = (process.env.REACT_APP_API_BASEURL || '').replace(/\/api\/?$/, '')

export type LotusCasinoGameCardProps = {
  game: Record<string, unknown>
  onPlay: (game: Record<string, unknown>) => void
  overrideImages?: Record<string, string>
  useCustomImage?: Record<string, boolean>
  /** Lobby / tab grid sizing (171×103) */
  compact?: boolean
  /** Eager-load above-the-fold card images */
  imagePriority?: boolean
  /** Show favorite toggle (off in PreDashboard pre-login) */
  showFavorite?: boolean
}

function resolveImageSources(
  game: Record<string, unknown>,
  overrideImages?: Record<string, string>,
  useCustomImage?: Record<string, boolean>,
): { primary: string; fallback: string } {
  const gid = String(game.game_identifier ?? game.identifier ?? game._id ?? '')
  if (gid && overrideImages?.[gid] && useCustomImage?.[gid]) {
    const override = overrideImages[gid]
    const primary = override.startsWith('http') ? override : `${API_BASE}${override}`
    return { primary, fallback: '' }
  }
  const localMac88 = resolveMac88LocalImageUrl(game)
  const remote = resolveMac88RemoteImageUrl(game)
  if (localMac88) return { primary: localMac88, fallback: remote }
  return { primary: remote, fallback: '' }
}

const LotusCasinoGameCard: React.FC<LotusCasinoGameCardProps> = ({
  game,
  onPlay,
  overrideImages,
  useCustomImage,
  compact = false,
  imagePriority = false,
  showFavorite = true,
}) => {
  const gameId = String(game.identifier ?? game.game_id ?? game._id ?? '')
  const [favorited, setFavorited] = useState(() => isCasinoFavorite(gameId))
  const { primary, fallback } = resolveImageSources(game, overrideImages, useCustomImage)
  const [imgSrc, setImgSrc] = useState(primary)
  const [imgFailed, setImgFailed] = useState(false)
  const gameName = String(game.game_name ?? game.name ?? '')

  useEffect(() => {
    setImgSrc(primary)
    setImgFailed(false)
  }, [primary, gameId])

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation()
    const next = toggleCasinoFavorite({
      gameId,
      identifier: game.identifier as string | number | undefined,
      game_name: gameName,
      game_images: imgSrc || primary,
      provider: game.provider as string | undefined,
    })
    setFavorited(next)
  }

  const handleImgError = () => {
    if (fallback && imgSrc !== fallback) {
      setImgSrc(fallback)
      return
    }
    setImgFailed(true)
  }

  return (
    <div className={`lotus-casino-card${compact ? ' lotus-casino-card--compact' : ''}`}>
      <div className="lotus-casino-card__container">
        {showFavorite && (
          <button
            type="button"
            className={`lotus-casino-card__favorite icon-my-markets-solid${favorited ? ' selected' : ''}`}
            aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
            onClick={handleFavorite}
          />
        )}
        <div className="lotus-casino-card__banner">
          {!imgFailed && imgSrc ? (
            <img
              src={imgSrc}
              alt={gameName}
              loading={imagePriority ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={imagePriority ? 'high' : 'auto'}
              onError={handleImgError}
            />
          ) : (
            <div className="lotus-casino-card__banner-placeholder" />
          )}
        </div>
        <div className="lotus-casino-card__actions">
          <button type="button" className="lotus-casino-card__play" onClick={() => onPlay(game)}>
            <i className="icon-play-second" aria-hidden />
            Play Now!
          </button>
        </div>
      </div>
    </div>
  )
}

export default LotusCasinoGameCard
